import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function SubslockPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            Subslock ("we," "our," or "us") is a Telegram paywall SaaS platform operated by
            Castle Point Innovations, a division of Cuitt LLC. Subslock enables creators to
            offer paywalled content accessible through Telegram bot integration, with
            subscription management and payment processing powered by Square.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect your
            information when you use the Subslock platform, including our website, Telegram
            bot, and related services. By using Subslock, you agree to the practices described
            in this policy.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following categories of information:</p>
          <ul>
            <li>
              <strong>Telegram Account Data:</strong> Your Telegram user ID, username, and
              display name as provided through the Telegram Bot API.
            </li>
            <li>
              <strong>Messaging Context:</strong> Telegram chat and messaging data necessary
              to deliver paywalled content and manage bot interactions.
            </li>
            <li>
              <strong>Subscription and Payment Information:</strong> Subscription status,
              plan details, payment history, and transaction records processed through Square.
            </li>
            <li>
              <strong>Square Payment Data:</strong> Billing information and payment method
              details collected and processed by Square on our behalf. We do not directly
              store full payment card numbers.
            </li>
            <li>
              <strong>User Preferences:</strong> Settings and configuration choices you make
              within the platform.
            </li>
            <li>
              <strong>Access Logs:</strong> Timestamps, IP addresses, and usage data
              generated when you interact with our services.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, operate, and maintain the Subslock platform and its features.</li>
            <li>Process subscriptions and facilitate payments through Square.</li>
            <li>
              Deliver paywalled content and manage access through Telegram bot interactions.
            </li>
            <li>Authenticate users and enforce subscription access controls.</li>
            <li>Communicate service updates, billing notifications, and support responses.</li>
            <li>Monitor platform performance, detect abuse, and maintain security.</li>
            <li>Comply with applicable legal obligations.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>
            Subslock relies on the following third-party services to operate. Each service may
            collect and process data in accordance with its own privacy policy:
          </p>
          <ul>
            <li>
              <strong>Square:</strong> Payment processing, billing, and transaction
              management. Square handles all sensitive payment data directly.
            </li>
            <li>
              <strong>Telegram Bot API:</strong> Messaging, content delivery, and user
              authentication within Telegram.
            </li>
            <li>
              <strong>Supabase (PostgreSQL):</strong> Primary data storage for user accounts,
              subscriptions, and platform records.
            </li>
            <li>
              <strong>Upstash Redis:</strong> Caching, session management, and rate limiting.
            </li>
            <li>
              <strong>Vercel:</strong> Hosting and delivery of the Subslock web frontend.
            </li>
            <li>
              <strong>Fly.io:</strong> Hosting for API services and background workers.
            </li>
          </ul>
          <p>
            We encourage you to review the privacy policies of these third-party providers to
            understand how they handle your data.
          </p>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your data is stored in cloud-hosted infrastructure provided by Supabase and
            Upstash, with application services running on Vercel and Fly.io. We implement
            reasonable technical and organizational safeguards to protect your information,
            including encryption in transit, access controls, and secure credential management.
          </p>
          <p>
            While we take appropriate measures to protect your data, no method of electronic
            storage or transmission is completely secure. We cannot guarantee absolute security
            of your information.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            We retain your information for as long as your account is active or as needed to
            provide our services. Subscription and payment records may be retained for a
            longer period as required for accounting, legal compliance, or dispute resolution
            purposes.
          </p>
          <p>
            Access logs and cached session data are retained for a limited period and are
            automatically purged on a rolling basis. If you request deletion of your account,
            we will remove your personal data within a reasonable timeframe, except where
            retention is required by law.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate or incomplete data.</li>
            <li>Request deletion of your personal data.</li>
            <li>Object to or restrict certain processing of your data.</li>
            <li>Request a portable copy of your data.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at the email address listed
            below. We will respond to your request within a reasonable timeframe and in
            accordance with applicable law.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <p>
          Subslock is not intended for use by individuals under the age of 13. We do not
          knowingly collect personal information from children. If we become aware that we
          have collected data from a child under 13, we will take steps to delete that
          information promptly. If you believe a child has provided us with personal data,
          please contact us immediately.
        </p>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our
          practices or applicable law. When we make material changes, we will update the
          "Last Updated" date at the top of this page. Your continued use of Subslock after
          any changes constitutes your acceptance of the revised policy.
        </p>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or
          our data practices, please contact us
          at <a href="mailto:contact@castlepointinnovations.com">contact@castlepointinnovations.com</a>.
        </p>
      ),
    },
  ];

  return <PrivacyPolicy productName="Subslock" lastUpdated="March 27, 2026" sections={sections} />;
}

export default SubslockPrivacy;
